/**
 * Fondori — "Dobierz do zestawu" cart addon widget click handling.
 *
 * Loaded via <script type="module" src="..."> tag in snippets/cart-drawer.liquid, placed
 * OUTSIDE any dynamically re-rendered cart content (see cart-addon-widget.liquid for why: DOM
 * morphing never auto-executes injected <script> tags, and the tag must load unconditionally,
 * not just on cart_type == drawer, so /cart itself also gets the click handler).
 *
 * Lightbox lookup is scoped per widget instance (`.fondori-cart-addon-wrapper`), not a page-wide
 * id — Horizon's client-side cart refresh can leave duplicate widget/dialog copies in the DOM,
 * and a page-wide id would silently resolve to the wrong (stale) one.
 *
 * Native "Added to your cart" popup integration (2026-09-16): both add-to-cart paths below use
 * the same CartLinesUpdateEvent pattern as Horizon's own assets/product-form.js (dispatch on the
 * clicked button before the fetch, POST /cart/add.js, fetch a fresh /cart.js, resolve the
 * deferred promise with cart + detail.items) instead of window.location.reload(), so the theme's
 * cart-added-popup-component actually fires, and the header cart badge / drawer / cart page all
 * refresh themselves via the same event with no extra code here.
 *
 * Minigaleria w podglądzie zdjęcia (2026-09-17, na życzenie Daniela): produkty up-sell mają teraz
 * po kilka zdjęć (Daniel dodał dodatkowe zdjęcia w Shopify). Każdy klikalny wyzwalacz podglądu
 * (miniatura i nazwa produktu — .fondori-cart-addon-thumb) niesie teraz dodatkowy atrybut
 * `data-gallery`, JSON-owa lista wszystkich zdjęć produktu ([{src, alt}, ...], zbudowana w
 * cart-addon-widget.liquid z `p.images`). Przy otwieraniu lightboxa ten JSON jest parsowany i
 * renderowany jako pasek klikalnych miniatur (`.fondori-addon-lightbox__thumbs`) pod dużym
 * zdjęciem — kliknięcie miniatury w PASKU (osobna klasa `.fondori-addon-lightbox__thumb`, nie
 * mylić z `.fondori-cart-addon-thumb` z listy produktów) podmienia duże zdjęcie w obrębie już
 * otwartego okna, bez jego zamykania. Pasek jest budowany przez DOM API (createElement +
 * textContent/setAttribute), nie przez wstawianie stringów do innerHTML, mimo że tu i tak dane
 * pochodzą wyłącznie z własnego katalogu Shopify — prostszy nawyk niż ryzyko.
 *
 * Wyłączenie popupu przy dodawaniu z WNĘTRZA koszyka (2026-09-17, wieczorem, na życzenie
 * Daniela: "Jak dodajemy produkt z up-sell w koszyku to nie wyświetlajmy juz tego potwierdzenia
 * pop-up bo jest to totalnie bez sensu"). Widget nosi teraz `data-widget-context` na swoim
 * `.fondori-cart-addon-wrapper` ('product' na stronie produktowej, domyślnie 'cart' w
 * koszyku/na stronie /cart — patrz cart-addon-widget.liquid). Obie ścieżki dodawania do koszyka
 * poniżej odczytują ten atrybut i dołączają `fondoriSuppressPopup: true` do `detail`, gdy
 * kontekst NIE jest 'product' — assets/cart-added-popup.js sprawdza tę flagę i pomija pokazanie
 * popupu. CartLinesUpdateEvent jest wysyłane identycznie jak wcześniej w obu przypadkach, więc
 * odświeżanie listy produktów/sumy/licznika w nagłówku (component-cart-items.js, nasłuchujące na
 * samo zdarzenie, niezależnie od popupu) działa bez zmian.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__fondoriCartAddonBound) return;
  window.__fondoriCartAddonBound = true;

  /**
   * Fetches the fresh, full cart (post-add) the same way Horizon's own product-form.js does via
   * its #refreshCart() fallback path, so cart-added-popup-component gets an accurate
   * `detail.items` / `cart` to read from.
   * @returns {Promise<any>}
   */
  function fondoriRefreshCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
      function (res) {
        if (!res.ok) throw new Error('Failed to fetch cart: ' + res.status);
        return res.json();
      }
    );
  }

  /**
   * Hides this widget instance after a successful add, mirroring the server-side `has_addon`
   * check in cart-addon-widget.liquid.
   * @param {Element} originEl
   */
  function fondoriHideWidgetInstance(originEl) {
    var wrapper = originEl.closest('.fondori-cart-addon-wrapper');
    if (wrapper) wrapper.style.display = 'none';
  }

  /**
   * Reads this widget instance's render context ('product' on product pages, 'cart' by default
   * inside the cart drawer/page — see the `data-widget-context` attribute set in
   * cart-addon-widget.liquid from `f_widget_context`). Used to decide whether the native
   * "Added to your cart" popup should show for this particular add.
   * @param {Element} originEl
   * @returns {string}
   */
  function fondoriGetWidgetContext(originEl) {
    var wrapper = originEl.closest('.fondori-cart-addon-wrapper');
    return (wrapper && wrapper.getAttribute('data-widget-context')) || 'cart';
  }

  /**
   * Renders the gallery thumbnail strip inside an open (or about-to-open) lightbox, from the
   * `data-gallery` JSON carried by whichever `.fondori-cart-addon-thumb` triggered the preview.
   * Hides the strip entirely when the product has fewer than 2 images — no point showing a row
   * of thumbnails with nothing to pick between.
   * @param {Element} lightbox
   * @param {string} galleryRaw
   * @param {string} activeSrc
   */
  function fondoriRenderGalleryThumbs(lightbox, galleryRaw, activeSrc) {
    var thumbsWrap = lightbox.querySelector('.fondori-addon-lightbox__thumbs');
    if (!thumbsWrap) return;

    thumbsWrap.textContent = '';

    var gallery = [];
    if (galleryRaw) {
      try {
        gallery = JSON.parse(galleryRaw);
      } catch (err) {
        gallery = [];
      }
    }

    if (!Array.isArray(gallery) || gallery.length < 2) {
      thumbsWrap.hidden = true;
      return;
    }

    gallery.forEach(function (g) {
      if (!g || !g.src) return;
      var gbtn = document.createElement('button');
      gbtn.type = 'button';
      gbtn.className = 'fondori-addon-lightbox__thumb';
      if (g.src === activeSrc) gbtn.classList.add('is-active');
      gbtn.setAttribute('data-src', g.src);
      gbtn.setAttribute('data-alt', g.alt || '');

      var gimg = document.createElement('img');
      gimg.src = g.src;
      gimg.alt = g.alt || '';
      gimg.loading = 'lazy';
      gbtn.appendChild(gimg);

      thumbsWrap.appendChild(gbtn);
    });

    thumbsWrap.hidden = false;
  }

  document.addEventListener('click', function (e) {
    // Product thumbnail/name in the "Dobierz do zestawu" widget → open enlarged preview in a
    // lightbox, with its gallery thumbnail strip populated from data-gallery.
    var thumb = e.target.closest('.fondori-cart-addon-thumb');
    if (thumb) {
      e.preventDefault();
      var wrapper = thumb.closest('.fondori-cart-addon-wrapper');
      var lightbox = wrapper
        ? wrapper.querySelector('.fondori-addon-lightbox')
        : document.querySelector('.fondori-addon-lightbox');
      var lightboxImage = lightbox ? lightbox.querySelector('.fondori-addon-lightbox__image') : null;
      if (lightbox && lightboxImage) {
        var openSrc = thumb.getAttribute('data-full-src') || '';
        lightboxImage.src = openSrc;
        lightboxImage.alt = thumb.getAttribute('data-full-alt') || '';
        fondoriRenderGalleryThumbs(lightbox, thumb.getAttribute('data-gallery'), openSrc);
        var lightboxAdd = lightbox.querySelector('.fondori-addon-lightbox__add');
        if (lightboxAdd) {
          lightboxAdd.setAttribute('data-variant-id', thumb.getAttribute('data-variant-id') || '');
          lightboxAdd.disabled = false;
          lightboxAdd.textContent = lightboxAdd.getAttribute('data-label') || lightboxAdd.textContent;
        }
        if (typeof lightbox.showModal === 'function') {
          lightbox.showModal();
        }
      }
      return;
    }

    // Gallery thumbnail INSIDE the already-open lightbox → swap the large preview image without
    // closing the dialog. Distinct class/selector from the product-row thumb above on purpose.
    var galleryThumb = e.target.closest('.fondori-addon-lightbox__thumb');
    if (galleryThumb) {
      var galleryLightbox = galleryThumb.closest('.fondori-addon-lightbox');
      var galleryMainImage = galleryLightbox
        ? galleryLightbox.querySelector('.fondori-addon-lightbox__image')
        : null;
      if (galleryMainImage) {
        galleryMainImage.src = galleryThumb.getAttribute('data-src') || '';
        galleryMainImage.alt = galleryThumb.getAttribute('data-alt') || '';
      }
      if (galleryLightbox) {
        var siblingThumbs = galleryLightbox.querySelectorAll('.fondori-addon-lightbox__thumb');
        Array.prototype.forEach.call(siblingThumbs, function (s) {
          s.classList.remove('is-active');
        });
      }
      galleryThumb.classList.add('is-active');
      return;
    }

    // "Add to cart" button inside the lightbox itself — adds just the previewed variant,
    // independent of whether its row's checkbox is checked.
    var lightboxAddBtn = e.target.closest('.fondori-addon-lightbox__add');
    if (lightboxAddBtn) {
      var previewVariantId = parseInt(lightboxAddBtn.getAttribute('data-variant-id'), 10);
      if (!previewVariantId) return;

      lightboxAddBtn.disabled = true;

      var lightboxWidgetContext = fondoriGetWidgetContext(lightboxAddBtn);

      var lightboxDeferred = CartLinesUpdateEvent.createPromise();
      lightboxAddBtn.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: previewVariantId, quantity: 1 }],
          promise: lightboxDeferred.promise,
        })
      );

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: previewVariantId, quantity: 1 }] }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw err;
            });
          }
          return res.json();
        })
        .then(function () {
          return fondoriRefreshCart().then(function (freshCart) {
            lightboxDeferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
              detail: {
                items: freshCart.items,
                source: 'fondori-cart-addon-widget',
                itemCount: freshCart.item_count,
                didError: false,
                fondoriSuppressPopup: lightboxWidgetContext !== 'product',
              },
            });
          });
        })
        .then(function () {
          var dialogToClose = lightboxAddBtn.closest('dialog');
          if (dialogToClose && typeof dialogToClose.close === 'function') dialogToClose.close();
          fondoriHideWidgetInstance(lightboxAddBtn);
        })
        .catch(function (err) {
          console.error('Fondori cart addon lightbox error', err);
          lightboxDeferred.reject(err);
          lightboxAddBtn.disabled = false;
        });
      return;
    }

    // Explicit close button inside the lightbox.
    var lightboxClose = e.target.closest('.fondori-addon-lightbox__close');
    if (lightboxClose) {
      var dialogToClose2 = lightboxClose.closest('dialog');
      if (dialogToClose2 && typeof dialogToClose2.close === 'function') dialogToClose2.close();
      return;
    }

    // Click on the backdrop area of the lightbox itself (outside the image/close button) closes it.
    if (
      e.target &&
      e.target.classList &&
      e.target.classList.contains('fondori-addon-lightbox') &&
      typeof e.target.close === 'function'
    ) {
      e.target.close();
      return;
    }

    var btn = e.target.closest('.fondori-cart-addon-cta');
    if (!btn) return;

    var widget = btn.closest('.fondori-cart-addon-widget');
    var checkboxes = widget ? widget.querySelectorAll('.fondori-cart-addon-checkbox') : [];
    var selected = Array.prototype.filter.call(checkboxes, function (cb) {
      return cb.checked;
    });
    if (selected.length === 0) return;

    var items = selected.map(function (cb) {
      return { id: parseInt(cb.getAttribute('data-variant-id'), 10), quantity: 1 };
    });

    if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Dodawanie…';

    var ctaWidgetContext = fondoriGetWidgetContext(btn);

    var ctaDeferred = CartLinesUpdateEvent.createPromise();
    btn.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(function (item) {
          return { merchandiseId: item.id, quantity: item.quantity };
        }),
        promise: ctaDeferred.promise,
      })
    );

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (err) {
            throw err;
          });
        }
        return res.json();
      })
      .then(function () {
        return fondoriRefreshCart().then(function (freshCart) {
          ctaDeferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(freshCart),
            detail: {
              items: freshCart.items,
              source: 'fondori-cart-addon-widget',
              itemCount: freshCart.item_count,
              didError: false,
              fondoriSuppressPopup: ctaWidgetContext !== 'product',
            },
          });
        });
      })
      .then(function () {
        fondoriHideWidgetInstance(btn);
      })
      .catch(function (err) {
        console.error('Fondori cart addon error', err);
        ctaDeferred.reject(err);
        btn.disabled = false;
        btn.textContent = btn.dataset.originalText || btn.textContent;
      });
  });

  // Free the loaded high-res image (and gallery thumbnails) once the lightbox closes (native
  // <dialog> "close" event does not bubble, so this must be a capturing listener on an ancestor
  // rather than a delegated bubbling one).
  document.addEventListener(
    'close',
    function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('fondori-addon-lightbox')) {
        var lightboxImage = e.target.querySelector('.fondori-addon-lightbox__image');
        if (lightboxImage) lightboxImage.src = '';
        var thumbsWrap = e.target.querySelector('.fondori-addon-lightbox__thumbs');
        if (thumbsWrap) {
          thumbsWrap.textContent = '';
          thumbsWrap.hidden = true;
        }
      }
    },
    true
  );
})();
